import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import StyledPanel from "../StyledPanel";
import { useStyles } from "./styles";
import { Grid, Typography } from "@material-ui/core";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import { isUndefined } from "lodash";
import clsx from "clsx";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";
import FlexibleParkingAccountForm from "../../Forms/ContractDetails/FlexibleParkingAccount";
import EditBalanceButton from "../../Buttons/EditBalance";
import EditPointsBalanceModal from "../../Modals/EditPointsBalance";
import ContractService from "../../../services/ContractService";
import RewardsPointService from "../../../services/RewardsPointService";
import apiClient from "../../../auth/apiClient";
import * as c from "../../../constants";
import CreditCardOnFileButton from "../../Buttons/CreditCardOnFile";
import { useLocation } from "react-router-dom";
import { useCardOnFile } from "../../../hooks/useCardOnFile";

const contractService = new ContractService(apiClient);
const rewardsPointService = new RewardsPointService(apiClient);

const FlexibleParkingAccountPanel = ({
  className,
  disabled,
  accessHolderID,
  facilityID,
  contractId,
  viewPermission,
  returnContext,
  isOneParkUser,
  pointsBalance,
  assignedAccessHolderName,
}) => {
  const classes = useStyles();
  const enqueueSnackbar = useEnqueueSnackbar();
  const theme = useTheme();
  const [contractID, setContractID] = useState(contractId);
  const [currentBalance, setCurrentBalance] = useState();
  const [fundID, setFundID] = useState();
  const [currentPoints, setCurrentPoints] = useState(pointsBalance ?? 0);
  const location = useLocation();
  const cardOnFileMessage = location.state?.cardOnFileMessage;
  const facilityGroupId = useSelector((state) => state.entityScope?.facilityGroupId);
  const scopeAwareFacilityID = facilityGroupId || facilityID;

  const [modalCardOnFileOpen, setModallCardOnFileOpen] = useState(false);
  const [pointsModalOpen, setPointsModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(c.FPA_PAYMENT_METHODS["CHARGE_FPA"]);
  const [contractInfo, setContractInfo] = useState();
  const { hasCardOnFile } = useCardOnFile(contractID);

  const handlePayMentMethodChange = (event) => {
    setPaymentMethod(event.target.value);
  }

  const toggleModalCardOnFileOpen = () => {
    setModallCardOnFileOpen(!modalCardOnFileOpen);
  };

  const updateContractInfo = (newContractInfo) => {
    setContractInfo(newContractInfo);
  }

  const handleSubmitPoints = async (result) => {
    const { points, type, note, metadata, firstName, lastName } = result;
    const prevPointBalance = currentPoints ?? 0;
    const delta = type === "Credit" ? points : -points;
    const updatedPointBalance = prevPointBalance + delta;
    const payload = {
      EntityId: scopeAwareFacilityID,
      AccessHolderId: accessHolderID,
      Points: points,
      Type: type === "Credit" ? "added" : "deducted",
      Notes: note,
      MetaData: metadata,
      AccessHolderName: `${firstName} ${lastName}`.trim(),
      FirstName: firstName,
      LastName: lastName,
    };
    try {
      const response = await rewardsPointService.adjustPoints(payload);
      setCurrentPoints(response?.data?.updatedPointsBalance ?? updatedPointBalance);
      setPointsModalOpen(false);
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to update points balance";
      enqueueSnackbar(message, {
        variant: "error",
        tag: "adjustPointsError",
      });
    }
  };

  const fetchBalance = useCallback(async () => {
    if (!contractID || !scopeAwareFacilityID) {
      setCurrentBalance(0);
      setFundID(null);
      return;
    }

    let response;
    try {
      response = await contractService.getFundBalance(scopeAwareFacilityID, contractID);

      if (response?.data) {
        setCurrentBalance(response.data?.balance);
        setFundID(response.data?.fundID);
      }
    } catch {
      enqueueSnackbar("Failed to retrieve funds balance", {
        variant: "error",
        tag: "getFlexibleParkingAccountFunds",
      });
      return;
    }
  }, [contractID, facilityID, enqueueSnackbar]);

  const displayCardOnFileErrMess = useCallback(async () => {
    if (!cardOnFileMessage) {
      return;
    }

    if (cardOnFileMessage.toLowerCase() == "success" || cardOnFileMessage.toLowerCase() == "pending") {
      toggleModalCardOnFileOpen();
      return;
    }
    if (cardOnFileMessage.toLowerCase() == "cancel") {
      enqueueSnackbar("Credit card on file was cancelled", {
        variant: "warning",
        tag: "getCardOnFileErrMess",
      });
      return;
    }
    if (cardOnFileMessage.toLowerCase() == "fail") {
      enqueueSnackbar("Credit card on file failed", {
        variant: "error",
        tag: "getCardOnFileErrMess",
      });
    }
  }, [cardOnFileMessage]);

  const fetchContractIdByAccessHolder = useCallback(async () => {
    if (!accessHolderID || !scopeAwareFacilityID) {
      setContractID();
      return;
    }

    let response;
    try {
      response = await contractService.getAccessHolder(
        scopeAwareFacilityID,
        accessHolderID
      );
      setContractID(response.data?.contractID);
    } catch {
      enqueueSnackbar("Failed to retrieve access holder status info", {
        variant: "error",
        tag: "accessHolderStatusInfoError",
      });
    }
  }, [accessHolderID, facilityID, enqueueSnackbar]);

  const fetchContractDetails = useCallback(async () => {
    if (!contractID || !facilityID) {
      setContractInfo();
      return;
    }
    let response;

    try {
      response = await contractService.getContractDetails(
        facilityID,
        contractID,
        c.CONTRACT_DETAILS_TYPE["Flexible Parking Account"]
      );
    } catch {
      enqueueSnackbar("Failed to retrieve Flexible Parking Account", {
        variant: "error",
        tag: "getFlexibleParkingAccount",
      });
      return;
    }

    if (response?.data?.payload) {
      setContractInfo(response.data);
      setPaymentMethod(response.data.payload?.paymentMethod);
    }
  }, [contractID, facilityID]);

  useEffect(() => {
    if (!isUndefined(contractID) && !isUndefined(facilityID)) {
      fetchContractDetails();
    }
  }, [facilityID, contractID, fetchContractDetails]);

  useEffect(() => {
    if (
      !isUndefined(accessHolderID) &&
      !isUndefined(facilityID) &&
      viewPermission
    ) {
      fetchContractIdByAccessHolder();
    }
  }, [facilityID, accessHolderID, facilityGroupId]);

  useEffect(() => {
    setCurrentPoints(pointsBalance ?? 0);
  }, [pointsBalance]);

  useEffect(() => {
    if (!isUndefined(contractID) && viewPermission) {
      fetchBalance();
    }
  }, [contractID, fetchBalance]);

  useEffect(() => {
    if (!isUndefined(cardOnFileMessage) && viewPermission) {
      displayCardOnFileErrMess();
    }
  }, [cardOnFileMessage, displayCardOnFileErrMess, viewPermission]);

  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <>
      <StyledPanel
        className={clsx("flexible-account-panel", className)}
        headerContent={
          <div className={clsx("header", classes.header)}>
            <div className={clsx("header-title")}>
              <Typography className={clsx("header-left", classes.headerLeft)}>
                {smallScreen ? "Flex" : "Flexible Parking Account"}
              </Typography>
            </div>
            <div
              data-testid="balance"
              className={clsx("header-balance", classes.headerBalance)}
            >
              Balance $
              {isUndefined(currentBalance) ? "0.00" : currentBalance.toFixed(2)}
            </div>
          </div>
        }
        cardContent={
          <Grid container item xs={12} lg={12} spacing={2}>
            <Grid item xs={12} lg={9}>
              <FlexibleParkingAccountForm
                contractID={contractID}
                facilityID={facilityID}
                disabled={disabled}
                paymentMethod={paymentMethod}
                handlePayMentMethodChange={handlePayMentMethodChange}
                contractInfo={contractInfo}
                updateContractInfo={updateContractInfo}
                currentBalance={currentBalance}
                entityID={scopeAwareFacilityID}
                accessHolderID={accessHolderID}
                isOneParkUser={isOneParkUser}
                pointsBalance={currentPoints}
                onOpenPointsModal={() => setPointsModalOpen(true)}
                assignedAccessHolderName={assignedAccessHolderName}
              />
            </Grid>
            <Grid item xs={12} lg={3} className={classes.actionsColumn}>
              <Grid container item xs={12} spacing={2} alignItems="flex-start" justify="flex-end">
                {(!(currentBalance === 0 && paymentMethod === c.FPA_PAYMENT_METHODS["CHARGE_CCOF"])) && (
                  <Grid item>
                    <EditBalanceButton
                      currentBalance={currentBalance}
                      setCurrentBalance={setCurrentBalance}
                      contractID={contractID}
                      entityID={facilityID}
                      fundType={c.FUND_TYPE.Amount}
                      fundID={fundID}
                      setFundID={setFundID}
                      disabled={disabled}
                      hasCardOnFile={hasCardOnFile}
                      accessHolderID={accessHolderID}
                    />
                  </Grid>
                )}
                <Grid item>
                  <CreditCardOnFileButton
                    contractID={contractID}
                    accessHolderID={accessHolderID}
                    disabled={disabled}
                    toggleModal={toggleModalCardOnFileOpen}
                    modalOpen={modalCardOnFileOpen}
                    returnContext={returnContext}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        }
      />
      <EditPointsBalanceModal
        open={pointsModalOpen}
        onClose={() => setPointsModalOpen(false)}
        currentPoints={currentPoints ?? 0}
        onSubmitPoints={handleSubmitPoints}
        accessHolderID={accessHolderID}
        accessHolderName={assignedAccessHolderName}
      />
    </>
  );
};

FlexibleParkingAccountPanel.propTypes = {
  className: PropTypes.string,
  contractID: PropTypes.string,
  facilityID: PropTypes.string,
  fundID: PropTypes.string,
  isOneParkUser: PropTypes.bool,
};

export default FlexibleParkingAccountPanel;
